import { Option } from "@inrev/core";
import { createContext, useContext } from "react";
import type { FileTracking } from "./tracking";

/** What a primitive that takes `testId` and `trackingId` needs to be tested and tracked. */
export type ControlIds = { testId: string; trackingId: string } & Partial<FileTracking>;

/**
 * The surface's test and tracking ids and the file on screen. Every part of the viewer builds
 * its controls' ids from these, so a control can't drop `file-id` from Amplitude.
 */
export type ViewerIds = {
	/** `<root test id>-<suffix>` */
	testId: (suffix: string) => string;
	/** `<root tracking id>-<suffix>` */
	trackingId: (suffix: string) => string;
	/** Spread on every tracked control, so Amplitude reports the file on screen. */
	fileTracking: Option<FileTracking>;
	/** All three at once, for a primitive that takes `testId` and `trackingId`. */
	control: (suffix: string) => ControlIds;
};

export const toViewerIds = (
	testId: string,
	trackingId: string,
	fileTracking: Option<FileTracking>,
): ViewerIds => ({
	testId: (suffix) => `${testId}-${suffix}`,
	trackingId: (suffix) => `${trackingId}-${suffix}`,
	fileTracking,
	control: (suffix) => ({
		testId: `${testId}-${suffix}`,
		trackingId: `${trackingId}-${suffix}`,
		...fileTracking,
	}),
});

/**
 * Provided by `DocumentViewer`. A container that composes its own chrome from the viewer's
 * parts provides it too, with a value from `toViewerIds`.
 */
export const ViewerIdsContext = createContext<Option<ViewerIds>>(undefined);

export const useViewerIds = (): ViewerIds => {
	const ids = useContext(ViewerIdsContext);
	if (Option.isNone(ids))
		throw new Error("Document viewer parts must render inside a ViewerIdsContext provider.");
	return ids;
};
